import { FC, useRef, useState } from "react";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Modal } from "@/components/Modal";
import Text from "@/components/Text";
import Button from "@/components/Button";
import { CloseButton, StyledDialog } from "@/components/AlertDialogs/Dialog";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { Textarea } from "@/components/Forms";

const Dialog = styled(StyledDialog)`
  padding-bottom: 24px;
  height: 348px;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 20px;
`;

const StyledTextarea = styled(Textarea)`
  margin-top: 20px;
`;

interface Props {
  onClose: () => void;
  mediaId: number;
}

const PromptModal: FC<Props> = ({ onClose, mediaId }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      await api.post(`/v1/image-generation/${mediaId}/additional-variations`, {
        type: "prompt",
        prompt,
      });

      await queryClient.invalidateQueries(
        ["image_generation_project", { id }],
        {
          exact: true,
        }
      );

      onClose();
      alerts.success("New variations is being generated");
    } catch (_) {
      setIsLoading(false);
      alerts.error();
    }
  };

  return (
    <Modal>
      <Dialog
        ref={ref}
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Text $type="heading2">Additional prompt</Text>
        <StyledTextarea
          placeholder="Add prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <Footer>
          <Button
            color="primary"
            disabled={isLoading || !prompt}
            loading={isLoading}
            onClick={onSubmit}
          >
            Generate
          </Button>
          <Button color="danger" onClick={onClose}>
            Cancel
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default PromptModal;
